
import { supabase } from '@/lib/supabase';
import { debugError, debugWarn } from '@/utils/debugLogger';
import { safeLocalStorage } from '@/utils/safeStorage';

/**
 * Service for authentication-related API calls
 */
export const authService = {
  /**
   * Sign in with email and password
   */
  signIn: async (email: string, password: string) => {
    
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      debugError('auth', "[AuthService] Login error:", error);
      throw error;
    }
    
    
    return data.user;
  },
  
  /**
   * Sign out the current user - with enhanced error handling
   * This method will always succeed from the UI perspective even if there are errors
   */
  signOut: async () => {
    
    
    // Clear any legacy admin status from localStorage (key deprecated, never set)
    try {
      safeLocalStorage.removeItem('gildy_admin_user');
    } catch(e) {
      debugError('auth', "[AuthService] Error clearing admin localStorage:", e);
    }
    
    // Function to check for active session without triggering errors
    const checkForActiveSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          debugError('auth', "[AuthService] Error checking session:", error);
          return false;
        }
        return !!data.session;
      } catch (e) {
        debugError('auth', "[AuthService] Exception checking session:", e);
        return false;
      }
    };
    
    try {
      // Check if we have an active session before attempting to sign out
      const hasSession = await checkForActiveSession();
      
      if (!hasSession) {
        
        return;
      }
      
      // Attempt signOut only if we have a session
      try {
        const { error } = await supabase.auth.signOut();
        if (error) {
          debugWarn('auth', "[AuthService] Supabase signOut returned error:", error);
          // Continue - we still want to clear local state
        } else {
          
        }
      } catch (signOutError) {
        // Catch and log errors but don't throw them
        debugWarn('auth', "[AuthService] Exception during signOut:", signOutError);
      }
    } catch (outerError) {
      debugError('auth', "[AuthService] Unexpected error in signOut process:", outerError);
    } finally {
      
      // We always consider logout a success from the UI perspective
    }
  },
  
  /**
   * Get current session
   */
  getCurrentSession: async () => {
    
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      debugError('auth', "[AuthService] Get session error:", error);
      throw error;
    }
    
    return data.session;
  },
  
  /**
   * Get current user
   */
  getCurrentUser: async () => {
    
    const { data, error } = await supabase.auth.getUser();
    
    if (error) {
      debugError('auth', "[AuthService] Get user error:", error);
      throw error;
    }
    
    return data.user;
  }
};
